import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import { API_URL, getAuthHeaders } from '../services/api';
import LimitWarning from '../components/LimitWarning';
import { platformLabel, platformIcon, platformColor } from '../utils/platform';

interface TeamMember {
  user_id: string;
  email: string;
  display_name: string | null;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'disabled';
  added_at: number;
  is_current_user: boolean;
}

interface TeamInstance {
  instance_id: string;
  instance_name?: string;
  agent_hostname?: string;
  running_status: string;
}

interface TeamInvitation {
  invitation_id: string;
  email: string;
  invited_by: string;
  created_at: number;
  expires_at: number;
}

const PAM_SCOPE_OPTIONS = [
  { key: 'destructive', label: 'Destructive Commands', description: 'File deletion, disk formatting, data destruction' },
  { key: 'credential', label: 'Credential Access', description: 'Reading passwords, tokens, API keys' },
  { key: 'system_admin', label: 'System Administration', description: 'Installing software, changing system settings' },
  { key: 'code_execution', label: 'Code Execution', description: 'Running scripts, compiling code' },
];

const PAM_ACTIONS = ['block', 'mfa_challenge', 'allow'];
const PAM_ACTION_LABELS: Record<string, string> = { block: 'Block', mfa_challenge: 'MFA Challenge', allow: 'Allow' };

function SecurityOverrideForm({ security, onSave, disabled }: {
  security: Record<string, unknown>;
  onSave: (security: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  const [local, setLocal] = useState<Record<string, unknown>>(security);

  const threatActions = (local.threat_actions || {}) as Record<string, string>;
  const pamScopes = (local.pam_scopes || {}) as Record<string, { enabled: boolean; action: string }>;
  const ACTIONS = ['block', 'request', 'log', 'ignore'];
  const SEVERITIES = ['critical', 'high', 'medium', 'low'];

  const updatePamScope = (key: string, field: 'enabled' | 'action', value: boolean | string) => {
    const current = pamScopes[key] || { enabled: false, action: 'mfa_challenge' };
    setLocal({
      ...local,
      pam_scopes: { ...pamScopes, [key]: { ...current, [field]: value } },
    });
  };

  return (
    <div className="space-y-3 text-xs">
      {/* Threat Detection */}
      <div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(local.enable_threat_detection)}
            onChange={(e) => setLocal({ ...local, enable_threat_detection: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-slate-300 text-primary-600" />
          <span className="text-slate-700 dark:text-slate-300 font-medium">Threat Detection</span>
        </label>
        {Boolean(local.enable_threat_detection) && (
          <div className="grid grid-cols-2 gap-1.5 ml-5 mt-1.5">
            {SEVERITIES.map((sev) => (
              <div key={sev} className="flex items-center gap-1.5">
                <span className="text-slate-500 dark:text-slate-400 capitalize w-14">{sev}:</span>
                <select value={threatActions[sev] || 'log'}
                  onChange={(e) => setLocal({ ...local, threat_actions: { ...threatActions, [sev]: e.target.value } })}
                  className="text-xs rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 px-1.5 py-0.5">
                  {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PAM */}
      <div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={Boolean(local.enable_pam)}
            onChange={(e) => setLocal({ ...local, enable_pam: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-slate-300 text-primary-600" />
          <span className="text-slate-700 dark:text-slate-300 font-medium">Privileged Access Management</span>
        </label>
        {Boolean(local.enable_pam) && (
          <div className="ml-5 mt-1.5 space-y-1.5">
            {PAM_SCOPE_OPTIONS.map((scope) => {
              const scopeConfig = pamScopes[scope.key] || { enabled: false, action: 'mfa_challenge' };
              return (
                <div key={scope.key} className="flex items-center gap-2">
                  <input type="checkbox" checked={scopeConfig.enabled}
                    onChange={(e) => updatePamScope(scope.key, 'enabled', e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-primary-600" />
                  <span className="text-slate-600 dark:text-slate-300 w-36" title={scope.description}>{scope.label}</span>
                  {scopeConfig.enabled && (
                    <select value={scopeConfig.action}
                      onChange={(e) => updatePamScope(scope.key, 'action', e.target.value)}
                      className="text-xs rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 px-1.5 py-0.5">
                      {PAM_ACTIONS.map((a) => <option key={a} value={a}>{PAM_ACTION_LABELS[a]}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
            {/* PAM Risk Actions */}
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <span className="text-slate-500 dark:text-slate-400 font-medium">Risk Level Actions</span>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {SEVERITIES.map((sev) => {
                  const riskActions = (local.pam_risk_actions || {}) as Record<string, string>;
                  return (
                    <div key={sev} className="flex items-center gap-1.5">
                      <span className="text-slate-500 dark:text-slate-400 capitalize w-14">{sev}:</span>
                      <select value={riskActions[sev] || (sev === 'critical' ? 'block' : sev === 'high' ? 'mfa_challenge' : 'allow')}
                        onChange={(e) => setLocal({ ...local, pam_risk_actions: { ...riskActions, [sev]: e.target.value } })}
                        className="text-xs rounded border-slate-300 dark:border-slate-600 dark:bg-slate-700 px-1.5 py-0.5">
                        {PAM_ACTIONS.map((a) => <option key={a} value={a}>{PAM_ACTION_LABELS[a]}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <button onClick={() => onSave(local)} disabled={disabled}
        className="mt-1 px-3 py-1 text-xs font-medium rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
        Save Security Settings
      </button>
    </div>
  );
}

export default function TeamPage() {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [error, setError] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [workspaceModalUserId, setWorkspaceModalUserId] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { firebaseUser } = useAuth();
  const { currentTeam } = useTeam();

  // Fetch team members
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['team', currentTeam?.team_id, 'members'],
    queryFn: async () => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/members`, {
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) throw new Error('Failed to fetch team members');
      return res.json();
    },
    enabled: !!firebaseUser
  });

  // Fetch pending invitations
  const { data: invitationsData } = useQuery({
    queryKey: ['team', currentTeam?.team_id, 'invitations'],
    queryFn: async () => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invitations`, {
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) throw new Error('Failed to fetch invitations');
      return res.json();
    },
    enabled: !!firebaseUser
  });

  // Fetch team platform identities (admin/owner only)
  const { data: teamIdentitiesData } = useQuery({
    queryKey: ['team', currentTeam?.team_id, 'identities'],
    queryFn: async () => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/identity/team`, {
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) return { identities: [] };
      return res.json();
    },
    enabled: !!firebaseUser
  });

  // Fetch team instances (for workspace assignment modal)
  const { data: instancesData } = useQuery({
    queryKey: ['instances'],
    queryFn: async () => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/instances`, {
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) throw new Error('Failed to fetch instances');
      return res.json();
    },
    enabled: !!firebaseUser
  });

  const teamInstances: TeamInstance[] = instancesData?.instances || [];

  // Build userId → identity[] map
  const userIdentityMap: Record<string, any[]> = {};
  for (const id of (teamIdentitiesData?.identities || [])) {
    if (!userIdentityMap[id.user_id]) userIdentityMap[id.user_id] = [];
    userIdentityMap[id.user_id].push(id);
  }

  // Fetch workspace access for all instances (to show inline access badges)
  const accessQueries = useQueries({
    queries: teamInstances.map((inst) => ({
      queryKey: ['instance', inst.instance_id, 'access'],
      queryFn: async () => {
        const token = await firebaseUser?.getIdToken();
        const res = await fetch(`${API_URL}/api/v1/instances/${inst.instance_id}/access`, {
          headers: getAuthHeaders(token!),
        });
        if (!res.ok) return { instance_id: inst.instance_id, access: [] };
        const data = await res.json();
        return { instance_id: inst.instance_id, access: data.access || [] };
      },
      enabled: !!firebaseUser && teamInstances.length > 0,
    })),
  });

  // Build userId → instance_id[] access map
  const userAccessMap: Record<string, string[]> = {};
  for (const q of accessQueries) {
    if (q.data) {
      for (const a of q.data.access as { user_id: string }[]) {
        if (!userAccessMap[a.user_id]) userAccessMap[a.user_id] = [];
        userAccessMap[a.user_id].push(q.data.instance_id);
      }
    }
  }

  // Fetch channel access for expanded member's accessible instances
  const expandedMember = (membersData?.members || []).find((m: TeamMember) => m.user_id === expandedUserId);
  const expandedHasFullAccess = expandedMember && (expandedMember.role === 'owner' || expandedMember.role === 'admin');
  const expandedAccessIds = expandedUserId ? (userAccessMap[expandedUserId] || []) : [];

  const memberChannelQueries = useQueries({
    queries: expandedUserId && !expandedHasFullAccess
      ? expandedAccessIds.map((instanceId) => ({
          queryKey: ['instance', instanceId, 'access', expandedUserId, 'channels'],
          queryFn: async () => {
            const token = await firebaseUser?.getIdToken();
            const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${expandedUserId}/channels`, {
              headers: getAuthHeaders(token!),
            });
            if (!res.ok) return { channels: [] };
            return res.json() as Promise<{ channels: Array<{ channel_type: string; workspace_enabled: boolean; member_enabled: boolean }> }>;
          },
          enabled: !!firebaseUser,
        }))
      : [],
  });

  // Build instanceId → channels map for easy lookup
  const memberChannelMap: Record<string, Array<{ channel_type: string; workspace_enabled: boolean; member_enabled: boolean }>> = {};
  expandedAccessIds.forEach((instId, idx) => {
    const data = memberChannelQueries[idx]?.data;
    if (data?.channels) memberChannelMap[instId] = data.channels;
  });

  // Fetch security overrides for expanded member's accessible instances
  const memberSecurityQueries = useQueries({
    queries: expandedUserId && !expandedHasFullAccess
      ? expandedAccessIds.map((instanceId) => ({
          queryKey: ['instance', instanceId, 'access', expandedUserId, 'security'],
          queryFn: async () => {
            const token = await firebaseUser?.getIdToken();
            const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${expandedUserId}/security`, {
              headers: getAuthHeaders(token!),
            });
            if (!res.ok) return { override_enabled: false, security: null };
            return res.json() as Promise<{ override_enabled: boolean; security: Record<string, unknown> | null }>;
          },
          enabled: !!firebaseUser,
        }))
      : [],
  });

  const memberSecurityMap: Record<string, { override_enabled: boolean; security: Record<string, unknown> | null }> = {};
  expandedAccessIds.forEach((instId, idx) => {
    const data = memberSecurityQueries[idx]?.data;
    if (data) memberSecurityMap[instId] = data;
  });

  // Admin unlink identity mutation
  const unlinkIdentityMutation = useMutation({
    mutationFn: async ({ targetUserId, identityId }: { targetUserId: string; identityId: string }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/identity/admin/${targetUserId}/${identityId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token!),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to unlink identity');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', currentTeam?.team_id, 'identities'] });
    },
  });

  // Toggle workspace access inline
  const toggleAccessMutation = useMutation({
    mutationFn: async ({ instanceId, userId, revoke }: { instanceId: string; userId: string; revoke: boolean }) => {
      const token = await firebaseUser?.getIdToken();
      if (revoke) {
        const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${userId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(token!),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to revoke access');
      } else {
        const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access`, {
          method: 'POST',
          headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to grant access');
      }
      return { instanceId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['instance', data.instanceId, 'access'] });
    },
  });

  const updateChannelAccessMutation = useMutation({
    mutationFn: async ({ instanceId, userId, channels }: { instanceId: string; userId: string; channels: Record<string, boolean> }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${userId}/channels`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
        body: JSON.stringify({ channels }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update channel access');
      return { instanceId, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['instance', data.instanceId, 'access', data.userId, 'channels'] });
    },
  });

  const updateSecurityOverrideMutation = useMutation({
    mutationFn: async ({ instanceId, userId, overrideEnabled, security }: {
      instanceId: string; userId: string; overrideEnabled: boolean; security?: Record<string, unknown>;
    }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${userId}/security`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_enabled: overrideEnabled, security }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update security override');
      return { instanceId, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['instance', data.instanceId, 'access', data.userId, 'security'] });
    },
  });

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invite`, {
        method: 'POST',
        headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send invitation');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setInviteModalOpen(false);
      setEmailInput('');
      setInviteRole('member');
      setError(null);

      // Show helpful message if email wasn't sent
      if (!data.email_sent) {
        // You could show a toast notification here
        console.info('Email not configured. Use "Copy Link" button to share invitation manually.');
      }
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/members/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) throw new Error('Failed to remove member');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    }
  });

  // Change role mutation
  const changeRoleMutation = useMutation({
    mutationFn: async ({ targetUserId, role }: { targetUserId: string; role: string }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/members/${targetUserId}/role`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to change role');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  });

  // Toggle member status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ targetUserId, status }: { targetUserId: string; status: 'active' | 'disabled' }) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/members/${targetUserId}/status`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to change status');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  });

  // Cancel invitation mutation
  const cancelMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token!)
      });
      if (!res.ok) throw new Error('Failed to cancel invitation');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', currentTeam?.team_id, 'invitations'] });
    }
  });

  // Resend invitation mutation
  const resendMutation = useMutation({
    mutationFn: async (email: string) => {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invite`, {
        method: 'POST',
        headers: getAuthHeaders(token!),
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to resend invitation');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', currentTeam?.team_id, 'invitations'] });
    }
  });

  const handleInvite = () => {
    if (!emailInput.trim()) {
      setError('Please enter an email address');
      return;
    }
    inviteMutation.mutate({ email: emailInput, role: inviteRole });
  };

  const copyInvitationLink = (invitationId: string) => {
    const inviteUrl = `${window.location.origin}/team/accept?invitation_id=${invitationId}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopiedInviteId(invitationId);
    setTimeout(() => setCopiedInviteId(null), 2000);
  };

  const members: TeamMember[] = membersData?.members || [];
  const invitations = invitationsData?.invitations || [];

  // Determine the current user's role from the members list
  const currentUserMember = members.find((m) => m.is_current_user);
  const currentUserRole = currentUserMember?.role;
  const canManageRoles = currentUserRole === 'owner' || currentUserRole === 'admin';

  const getRoleBadge = (role: TeamMember['role']) => {
    switch (role) {
      case 'owner':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
            Owner
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            Admin
          </span>
        );
      case 'member':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            Member
          </span>
        );
    }
  };

  return (
    <Layout title="Team" subtitle={canManageRoles ? "Manage members and team-wide defaults" : "Your team members"}>
      {/* Seat Limit Warning */}
      <LimitWarning type="seat" />

      {/* Team Overview Matrix */}
      {canManageRoles && members.length > 0 && teamInstances.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Team Overview</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Member access to workspaces and linked channel accounts.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Member</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Channels</th>
                  {teamInstances.map((inst) => (
                    <th key={inst.instance_id} className="text-center py-2 px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          inst.running_status === 'running' ? 'bg-emerald-400' : inst.running_status === 'error' ? 'bg-red-400' : 'bg-slate-400'
                        }`} />
                        {inst.instance_name || inst.agent_hostname || 'Workspace'}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((member: TeamMember) => {
                  const identities = userIdentityMap[member.user_id] || [];
                  const memberAccessIds = userAccessMap[member.user_id] || [];
                  const hasFullAccess = member.role === 'owner' || member.role === 'admin';
                  return (
                    <tr key={member.user_id} className={`border-b border-slate-100 dark:border-slate-800 ${member.status === 'disabled' ? 'opacity-40' : ''}`}>
                      <td className="py-2.5 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {(member.display_name || member.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{member.display_name || member.email}</p>
                          </div>
                          {getRoleBadge(member.role)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        {identities.length > 0 ? (
                          <div className="flex items-center gap-1">
                            {identities.map((id: any) => {
                              const meta = id.platform_meta || {};
                              const displayName = meta.username || meta.display_name || id.platform_user_id;
                              return (
                                <span
                                  key={id.identity_id}
                                  title={`${platformLabel(id.platform)}: ${displayName}${id.verified ? '' : ' (pending)'}`}
                                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                    id.verified ? platformColor(id.platform) : 'bg-yellow-500'
                                  }`}
                                >
                                  {platformIcon(id.platform)}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">--</span>
                        )}
                      </td>
                      {teamInstances.map((inst) => {
                        const hasAccess = hasFullAccess || memberAccessIds.includes(inst.instance_id);
                        return (
                          <td key={inst.instance_id} className="py-2.5 px-3 text-center">
                            {hasAccess ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Team Members Section */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Team Members</h2>
          {canManageRoles && (
            <button
              onClick={() => setInviteModalOpen(true)}
              className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              Invite Member
            </button>
          )}
        </div>

        {membersLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : members.length > 0 ? (
          <div className="space-y-3">
            {members.map((member: TeamMember) => {
              const identities = userIdentityMap[member.user_id] || [];
              const memberAccessIds = userAccessMap[member.user_id] || [];
              const isExpanded = expandedUserId === member.user_id;
              const hasFullAccess = member.role === 'owner' || member.role === 'admin';
              return (
              <div
                key={member.user_id}
                className={`bg-slate-50 dark:bg-slate-800 rounded-lg${member.status === 'disabled' ? ' opacity-60' : ''}`}
              >
                {/* Main row */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <button
                      onClick={() => setExpandedUserId(isExpanded ? null : member.user_id)}
                      className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold shadow-md flex-shrink-0 hover:opacity-90 transition-opacity"
                      title={isExpanded ? 'Collapse details' : 'Expand details'}
                    >
                      {(member.display_name || member.email).charAt(0).toUpperCase()}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center space-x-2 flex-wrap">
                        <p className="font-medium text-slate-900 dark:text-white">
                          {member.display_name || member.email}
                        </p>
                        {getRoleBadge(member.role)}
                        {member.status === 'disabled' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            Disabled
                          </span>
                        )}
                        {identities.length > 0 && (
                          <div className="flex items-center gap-1">
                            {identities.map((id: any) => (
                              <span
                                key={id.identity_id}
                                title={`${platformLabel(id.platform)}: ${id.platform_user_id}${id.verified ? '' : ' (pending)'}`}
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                  id.verified ? platformColor(id.platform) : 'bg-yellow-500'
                                }`}
                              >
                                {platformIcon(id.platform)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {member.display_name && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">{member.email}</p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {member.is_current_user
                            ? 'You'
                            : `Joined ${new Date(member.added_at).toLocaleDateString()}`}
                        </p>
                        {/* Inline workspace access summary */}
                        {hasFullAccess ? (
                          <span className="text-xs text-slate-400 dark:text-slate-500">All workspaces</span>
                        ) : memberAccessIds.length > 0 ? (
                          <span className="text-xs text-primary-600 dark:text-primary-400">
                            {memberAccessIds.length} workspace{memberAccessIds.length !== 1 ? 's' : ''}
                          </span>
                        ) : member.role === 'member' ? (
                          <span className="text-xs text-amber-600 dark:text-amber-400">No workspace access</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    {/* Expand/collapse button */}
                    <button
                      onClick={() => setExpandedUserId(isExpanded ? null : member.user_id)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {/* Role change dropdown */}
                    {canManageRoles && !member.is_current_user && member.role !== 'owner' && (
                      <select
                        value={member.role}
                        onChange={(e) => {
                          const newRole = e.target.value;
                          if (newRole !== member.role) {
                            changeRoleMutation.mutate({ targetUserId: member.user_id, role: newRole });
                          }
                        }}
                        disabled={changeRoleMutation.isPending}
                        className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                    {canManageRoles && !member.is_current_user && member.role !== 'owner' && (
                      <button
                        onClick={() => {
                          const newStatus = member.status === 'disabled' ? 'active' : 'disabled';
                          const action = newStatus === 'disabled' ? 'Disable' : 'Enable';
                          if (confirm(`${action} ${member.email}?`)) {
                            toggleStatusMutation.mutate({ targetUserId: member.user_id, status: newStatus });
                          }
                        }}
                        disabled={toggleStatusMutation.isPending}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                          member.status === 'disabled'
                            ? 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                            : 'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                        }`}
                      >
                        {member.status === 'disabled' ? 'Enable' : 'Disable'}
                      </button>
                    )}
                    {canManageRoles && !member.is_current_user && member.role !== 'owner' && (
                      <button
                        onClick={() => {
                          if (confirm(`Remove ${member.email} from team?`)) {
                            removeMutation.mutate(member.user_id);
                          }
                        }}
                        disabled={removeMutation.isPending}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium disabled:opacity-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700 pt-3 space-y-4">
                    {/* Linked Accounts */}
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Linked Accounts</h4>
                      {identities.length > 0 ? (
                        <div className="space-y-2">
                          {identities.map((id: any) => {
                            const meta = id.platform_meta || {};
                            const displayName = meta.username || meta.display_name || id.platform_user_id;
                            return (
                              <div key={id.identity_id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${id.verified ? platformColor(id.platform) : 'bg-yellow-500'}`}>
                                    {platformIcon(id.platform)}
                                  </span>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                                      {platformLabel(id.platform)}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                      {displayName}
                                      {!id.verified && <span className="ml-1 text-yellow-600 dark:text-yellow-400">(pending)</span>}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-400 dark:text-slate-500">
                                    {new Date(id.linked_at).toLocaleDateString()}
                                  </span>
                                  {canManageRoles && !member.is_current_user && (
                                    <button
                                      onClick={() => {
                                        if (confirm(`Unlink ${platformLabel(id.platform)} account (${displayName}) from ${member.email}?`)) {
                                          unlinkIdentityMutation.mutate({ targetUserId: member.user_id, identityId: id.identity_id });
                                        }
                                      }}
                                      disabled={unlinkIdentityMutation.isPending}
                                      className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                                      title="Unlink this account"
                                    >
                                      Unlink
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 dark:text-slate-500">No linked accounts</p>
                      )}
                    </div>

                    {/* Workspace Access */}
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Workspace Access</h4>
                      {hasFullAccess ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {member.role === 'owner' ? 'Owners' : 'Admins'} have access to all workspaces.
                        </p>
                      ) : teamInstances.length > 0 ? (
                        <div className="space-y-1.5">
                          {teamInstances.map((inst) => {
                            const hasAccess = memberAccessIds.includes(inst.instance_id);
                            const isToggling = toggleAccessMutation.isPending;
                            return (
                              <div key={inst.instance_id}>
                                <label
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                                    hasAccess
                                      ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                                      : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                                  } ${isToggling ? 'opacity-60 pointer-events-none' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {canManageRoles && !member.is_current_user ? (
                                      <input
                                        type="checkbox"
                                        checked={hasAccess}
                                        onChange={() => toggleAccessMutation.mutate({ instanceId: inst.instance_id, userId: member.user_id, revoke: hasAccess })}
                                        disabled={isToggling}
                                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                      />
                                    ) : (
                                      <span className={`w-2 h-2 rounded-full ${hasAccess ? 'bg-primary-500' : 'bg-slate-300'}`} />
                                    )}
                                    <span className="text-sm text-slate-900 dark:text-white">
                                      {inst.instance_name || inst.agent_hostname || inst.instance_id}
                                    </span>
                                  </div>
                                  <span className={`w-2 h-2 rounded-full ${
                                    inst.running_status === 'running' ? 'bg-emerald-400' : inst.running_status === 'error' ? 'bg-red-400' : 'bg-slate-400'
                                  }`} title={inst.running_status} />
                                </label>
                                {hasAccess && memberChannelMap[inst.instance_id]?.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-1 ml-6">
                                    {memberChannelMap[inst.instance_id].map((ch) => (
                                      <button
                                        key={ch.channel_type}
                                        onClick={() => {
                                          if (!canManageRoles || member.is_current_user) return;
                                          const newChannels: Record<string, boolean> = {};
                                          memberChannelMap[inst.instance_id].forEach((c) => {
                                            newChannels[c.channel_type] = c.channel_type === ch.channel_type ? !c.member_enabled : c.member_enabled;
                                          });
                                          updateChannelAccessMutation.mutate({ instanceId: inst.instance_id, userId: member.user_id, channels: newChannels });
                                        }}
                                        disabled={!canManageRoles || member.is_current_user || updateChannelAccessMutation.isPending}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                          ch.member_enabled
                                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                                            : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 line-through'
                                        } ${canManageRoles && !member.is_current_user ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                                      >
                                        {ch.member_enabled ? '\u2713' : '\u2717'} {ch.channel_type}
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {hasAccess && canManageRoles && !member.is_current_user && (() => {
                                  const secData = memberSecurityMap[inst.instance_id];
                                  if (!secData) return null;
                                  const isOverridden = secData.override_enabled;
                                  return (
                                    <div className="ml-6 mt-1.5">
                                      <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={isOverridden}
                                          onChange={() => {
                                            updateSecurityOverrideMutation.mutate({
                                              instanceId: inst.instance_id,
                                              userId: member.user_id,
                                              overrideEnabled: !isOverridden,
                                              security: !isOverridden ? (secData.security || {}) : undefined,
                                            });
                                          }}
                                          disabled={updateSecurityOverrideMutation.isPending}
                                          className="w-3.5 h-3.5 rounded border-slate-300 text-primary-600"
                                        />
                                        Custom security settings
                                      </label>
                                      {isOverridden && (
                                        <div className="mt-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                          <SecurityOverrideForm
                                            security={secData.security || {}}
                                            onSave={(security) => {
                                              updateSecurityOverrideMutation.mutate({
                                                instanceId: inst.instance_id,
                                                userId: member.user_id,
                                                overrideEnabled: true,
                                                security,
                                              });
                                            }}
                                            disabled={updateSecurityOverrideMutation.isPending}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 dark:text-slate-500">No workspaces configured</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Your team is just you</p>
            <p className="text-sm text-slate-400 dark:text-slate-500">Invite others to collaborate on your infrastructure</p>
          </div>
        )}
      </div>

      {/* Pending Invitations (admin/owner only) */}
      {canManageRoles && invitations.length > 0 && (
        <div className="mt-6 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Pending Invitations</h3>
          <div className="space-y-3">
            {invitations.map((inv: TeamInvitation) => (
              <div
                key={inv.invitation_id}
                className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{inv.email}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Invited {new Date(inv.created_at).toLocaleDateString()} · Expires{' '}
                    {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => copyInvitationLink(inv.invitation_id)}
                    className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    title="Copy invitation link"
                  >
                    {copiedInviteId === inv.invitation_id ? (
                      <>
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Resend invitation to ${inv.email}?`)) {
                        resendMutation.mutate(inv.email);
                      }
                    }}
                    disabled={resendMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                    title="Resend invitation email"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Resend
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Cancel invitation to ${inv.email}?`)) {
                        cancelMutation.mutate(inv.invitation_id);
                      }
                    }}
                    disabled={cancelMutation.isPending}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {inviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Invite Team Member</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Team members will have full access to manage your agents, instances, and configurations.
            </p>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              placeholder="email@example.com"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg mb-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
            )}
            <div className="flex space-x-3">
              <button
                onClick={handleInvite}
                disabled={!emailInput || inviteMutation.isPending}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </button>
              <button
                onClick={() => {
                  setInviteModalOpen(false);
                  setEmailInput('');
                  setInviteRole('member');
                  setError(null);
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workspace Assignment Modal */}
      {workspaceModalUserId && (
        <WorkspaceAssignmentModal
          userId={workspaceModalUserId}
          userEmail={members.find(m => m.user_id === workspaceModalUserId)?.email || ''}
          userDisplayName={members.find(m => m.user_id === workspaceModalUserId)?.display_name || null}
          instances={teamInstances}
          firebaseUser={firebaseUser}
          onClose={() => setWorkspaceModalUserId(null)}
        />
      )}
    </Layout>
  );
}

function WorkspaceAssignmentModal({
  userId,
  userEmail,
  userDisplayName,
  instances,
  firebaseUser,
  onClose,
}: {
  userId: string;
  userEmail: string;
  userDisplayName: string | null;
  instances: TeamInstance[];
  firebaseUser: any;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [togglingInstance, setTogglingInstance] = useState<string | null>(null);

  // Fetch access list for each instance
  const accessQueries = useQueries({
    queries: instances.map((inst) => ({
      queryKey: ['instance', inst.instance_id, 'access'],
      queryFn: async () => {
        const token = await firebaseUser?.getIdToken();
        const res = await fetch(`${API_URL}/api/v1/instances/${inst.instance_id}/access`, {
          headers: getAuthHeaders(token!),
        });
        if (!res.ok) throw new Error('Failed to fetch access');
        const data = await res.json();
        return { instance_id: inst.instance_id, access: data.access || [] };
      },
      enabled: !!firebaseUser,
    })),
  });

  const isLoading = accessQueries.some((q) => q.isLoading);

  // Build set of instance IDs this user has access to
  const accessibleInstanceIds = new Set<string>();
  for (const q of accessQueries) {
    if (q.data) {
      const hasAccess = q.data.access.some((a: { user_id: string }) => a.user_id === userId);
      if (hasAccess) {
        accessibleInstanceIds.add(q.data.instance_id);
      }
    }
  }

  const handleToggle = async (instanceId: string, currentlyHasAccess: boolean) => {
    setTogglingInstance(instanceId);
    try {
      const token = await firebaseUser?.getIdToken();
      if (currentlyHasAccess) {
        const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${userId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(token!),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Failed to revoke access' }));
          throw new Error(err.error || 'Failed to revoke access');
        }
      } else {
        const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access`, {
          method: 'POST',
          headers: { ...getAuthHeaders(token!), 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Failed to grant access' }));
          throw new Error(err.error || 'Failed to grant access');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['instance', instanceId, 'access'] });
    } catch (e) {
      console.error(e);
    } finally {
      setTogglingInstance(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'bg-emerald-400';
      case 'stopped': return 'bg-slate-400';
      case 'error': return 'bg-red-400';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Workspace Access</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {userDisplayName || userEmail}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Control which AI workspaces this member can access. Members can only interact with workspaces they've been explicitly granted access to. Owners and admins have access to all workspaces by default.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : instances.length === 0 ? (
          <p className="text-center py-8 text-slate-500 dark:text-slate-400">No workspaces found</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {instances.map((inst) => {
              const hasAccess = accessibleInstanceIds.has(inst.instance_id);
              const isToggling = togglingInstance === inst.instance_id;
              return (
                <label
                  key={inst.instance_id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    hasAccess
                      ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                      : 'bg-slate-50 dark:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                  } ${isToggling ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={hasAccess}
                      onChange={() => handleToggle(inst.instance_id, hasAccess)}
                      disabled={isToggling}
                      className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white text-sm">
                        {inst.instance_name || inst.agent_hostname || inst.instance_id}
                      </p>
                      {inst.instance_name && inst.agent_hostname && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{inst.agent_hostname}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isToggling && (
                      <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
                    )}
                    <span className={`w-2 h-2 rounded-full ${getStatusColor(inst.running_status)}`} title={inst.running_status} />
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Granting access allows the member to chat with AI in the selected workspace via the web console or linked messaging platforms.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium text-slate-800 dark:text-slate-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
